import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'

type StressFootprintInsight = {
  event_keyword: string
  sample_count: number
  avg_next_day_hrv_impact_pct: number | null
  avg_next_day_resting_hr_delta: number | null
}

type StressFootprintCardProps = {
  correlations: StressFootprintInsight[]
  embedded?: boolean
}

const StressFootprintCard = ({ correlations, embedded = false }: StressFootprintCardProps) => {
  const { t } = useTranslation()

  if (correlations.length === 0) return null

  const top = correlations.slice(0, 4)

  function formatHrvImpact(pct: number | null): string {
    if (pct == null) return '—'
    const display = Math.round(Math.abs(pct) * 100)
    const sign = pct < 0 ? '−' : '+'
    return t('coach.stressFootprint.hrvNextDay', { sign, display })
  }

  const shellClass = embedded ? KILO.embeddedSection : `col-span-2 ${KILO.inset}`

  return (
    <div className={shellClass}>
      <p className={`mb-1 ${KILO.sectionLabel}`}>{t('coach.stressFootprint.title')}</p>
      <p className="mb-3 text-sm text-neutral-500 dark:text-neutral-400">{t('coach.stressFootprint.description')}</p>
      <div className="space-y-2">
        {top.map((row) => (
          <div
            key={row.event_keyword}
            className="flex items-center justify-between rounded-xl border border-kilo-border bg-kilo-card/60 px-3 py-2"
          >
            <div>
              <span className={`text-sm font-medium capitalize ${KILO.text.primary}`}>{row.event_keyword}</span>
              <span className={`ml-2 text-xs ${KILO.text.soft}`}>
                {t('coach.stressFootprint.samples', { count: row.sample_count })}
              </span>
            </div>
            <span
              className={`text-xs font-medium ${
                (row.avg_next_day_hrv_impact_pct ?? 0) < -0.05 ? 'text-orange-400' : KILO.text.muted
              }`}
            >
              {formatHrvImpact(row.avg_next_day_hrv_impact_pct)}
              {row.avg_next_day_resting_hr_delta != null && row.avg_next_day_resting_hr_delta > 0
                ? ` · ${t('coach.stressFootprint.rhrDelta', { bpm: row.avg_next_day_resting_hr_delta })}`
                : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default StressFootprintCard
